import type { Stats } from "../api/types";
import type { MeaningClass } from "../lib/reportStore";
import { formatPct, formatTime } from "../lib/format";
import { SHOW_MEANING_ANALYSIS } from "../lib/flags";

// "Misread" combines substitutions + mispronunciations.
const CATS: Array<{ label: string; cls: string; value: (s: Stats) => number }> = [
  { label: "Misread", cls: "m-misread", value: (s) => (s.substitutions || 0) + (s.mispronunciations || 0) },
  { label: "Missing", cls: "m-missing", value: (s) => s.missing || 0 },
  { label: "Added", cls: "m-added", value: (s) => s.added || 0 },
  { label: "Pauses", cls: "m-pause", value: (s) => s.pauses || 0 },
];

const MEANINGS: Array<{ key: MeaningClass | "unset"; label: string }> = [
  { key: "substantial", label: "Major" },
  { key: "slight", label: "Minor" },
  { key: "none", label: "No change" },
  { key: "unset", label: "Unrated" },
];

interface Props {
  stats: Stats;
  // Impact-on-Meaning (premium) — now inline in the strip.
  analyzed: boolean;
  available: boolean;
  running: boolean;
  onRun: (force: boolean) => void;
  meaningCounts: Record<string, number>;
  activeMeanings: Set<string>;
  onToggleMeaning: (m: string) => void;
  // Audio start / end review — toggles for the right-hand drawer.
  hasBoundary: boolean;
  startConfirmed: boolean;
  endConfirmed: boolean;
  openBoundary: "start" | "end" | null;
  onToggleBoundary: (which: "start" | "end") => void;
}

/**
 * The report's single top strip: Match rate + category counts on the left, the
 * Impact-on-Meaning filter (subtly set apart) in the middle, and the
 * Audio-start / Audio-end drawer toggles pinned to the right corner (over the
 * findings pane). Replaces the separate StatsBar + AnalysisBar.
 */
export function ReportStrip(p: Props) {
  const { stats } = p;
  return (
    <div className="rstrip">
      <div
        className="stat-big"
        title={`Match rate — the share of words that match the manuscript (100% − WER, the speech-recognition word error rate). WER: ${formatPct(stats.wer || 0, 2)}.`}
      >
        <div className="num">{formatPct(Math.max(0, 1 - (stats.wer || 0)), 2)}</div>
        <div className="lbl">Match</div>
      </div>

      <div className="stat-chips">
        {CATS.map((c) => (
          <span key={c.label} className="stat-chip">
            <span className={`swatch ${c.cls}`} />
            {c.label}: <strong>{c.value(stats)}</strong>
          </span>
        ))}
      </div>

      {SHOW_MEANING_ANALYSIS && (
        <>
          <span className="rstrip-div" />

          <div className="impact-inline impact-unset">
            <span className="impact-inline-title">Meaning Impact</span>
            <div className="impact-pills">
              {MEANINGS.map((m) => {
                const on = p.activeMeanings.has(m.key);
                return (
                  <button
                    key={m.key}
                    className={`impact-pill impact-${m.key} ${on ? "on" : "off"}`}
                    onClick={() => p.onToggleMeaning(m.key)}
                    title={on ? `Hide “${m.label}”` : `Show “${m.label}”`}
                  >
                    <span className="ip-n">{p.meaningCounts[m.key] || 0}</span>
                    <span className="ip-l">{m.label}</span>
                  </button>
                );
              })}
            </div>
            {p.running ? (
              <span className="muted impact-state">
                <span className="spinner" style={{ width: 11, height: 11 }} /> analyzing…
              </span>
            ) : p.available ? (
              <button className="btn mc-run sm" onClick={() => p.onRun(false)}>
                {p.analyzed ? "Re-analyze" : "Run analysis"}
              </button>
            ) : (
              <span className="muted impact-state" title="Set ANTHROPIC_API_KEY on the server">needs API key</span>
            )}
          </div>
        </>
      )}

      <div className="rstrip-right">
        <span className="muted rstrip-words">
          {stats.transcript_words?.toLocaleString()} words · {formatTime(stats.audio_duration ?? null)}
        </span>
        {p.hasBoundary && (
          <div className="bound-toggles">
            <button
              className={`bound-toggle${p.openBoundary === "start" ? " open" : ""}${p.startConfirmed ? " ok" : " warn"}`}
              onClick={() => p.onToggleBoundary("start")}
              title="Review where the audio begins in the manuscript"
            >
              {p.startConfirmed ? "✓" : "⚑"} Audio start
            </button>
            <button
              className={`bound-toggle${p.openBoundary === "end" ? " open" : ""}${p.endConfirmed ? " ok" : " warn"}`}
              onClick={() => p.onToggleBoundary("end")}
              title="Review where the audio ends in the manuscript"
            >
              {p.endConfirmed ? "✓" : "⚑"} Audio end
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
